import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
    Sparkle, Airplane, MapPin, ForkKnife, CarProfile, Boat, Ticket, Heart, PaperPlaneRight,
    ArrowLeft, Share, BookmarkSimple, Trash, ArrowClockwise, Users, Wallet, Plus, Calendar, GlobeHemisphereWest,
} from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { conciergeApi, authApi } from "@/lib/apiClient";
import { PassportLogo } from "@/components/Reveal";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { usePassportShare } from "@/components/messages/PassportShareProvider";

export const KIND_ICONS = {
    flight: Airplane,
    hotel: BookmarkSimple,
    ground: CarProfile,
    cruise: Boat,
    dining: ForkKnife,
    activity: Ticket,
    traveldate: Heart,
};

export const KIND_COLORS = {
    flight: "#9FD5D0",
    hotel: "#D4AF37",
    ground: "#F4A28F",
    cruise: "#9FD5D0",
    dining: "#F4A28F",
    activity: "#D4AF37",
    traveldate: "#F4A28F",
};

const QUICK_MODE_KEYS = [
    { k: "better", icon: "✨" },
    { k: "cheaper", icon: "💰" },
    { k: "luxury", icon: "✨" },
    { k: "romantic", icon: "❤️" },
    { k: "family", icon: "👨‍👩‍👧" },
    { k: "adventurous", icon: "🏔" },
    { k: "foodie", icon: "🍽" },
    { k: "nightlife", icon: "🌙" },
];

const STATUSES = ["planning", "booked", "upcoming", "in_progress", "completed", "cancelled"];

export default function TripDashboard() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user, setUser, loading: authLoading } = useAuth();
    const { openPassportShare } = usePassportShare();
    const { t } = useTranslation();
    const [trip, setTrip] = useState(null);
    const [chat, setChat] = useState([]);
    const [versionsCount, setVersionsCount] = useState(0);
    const [msg, setMsg] = useState("");
    const [busy, setBusy] = useState(false);
    const [modeBusy, setModeBusy] = useState(null);
    const [showShare, setShowShare] = useState(false);
    const [duplicatingTrip, setDuplicatingTrip] = useState(false);
    const chatRef = useRef(null);

    const load = useCallback(async () => {
        try {
            const tData = await conciergeApi.getTrip(id);
            setTrip(tData);
            setChat(tData.chat_history || []);
            setVersionsCount((tData.versions || []).length);
        } catch (e) {
            toast.error(t("tripDashboard.toasts.notFound"));
            navigate("/concierge");
        }
    }, [id, navigate, t]);

    const shareInMessages = () => {
        if (!trip?.id) return;
        openPassportShare({ shareRef: { entity_type: "trip", entity_id: trip.id }, sourceLabel: "this Trip" });
    };

    useEffect(() => { if (user?.id) load(); }, [user?.id, load]);
    useEffect(() => { chatRef.current?.scrollTo({ top: chatRef.current.scrollHeight, behavior: "smooth" }); }, [chat, busy]);

    const groupedItems = useMemo(() => {
        const map = {};
        for (const it of trip?.items || []) {
            const k = it.kind;
            if (!map[k]) map[k] = [];
            map[k].push(it);
        }
        return map;
    }, [trip?.items]);

    if (authLoading) return <div className="min-h-screen grid place-items-center text-white/60">{t("tripDashboard.boarding")}</div>;
    if (!user) { navigate("/auth"); return null; }
    if (!trip) return <div className="min-h-screen grid place-items-center text-white/60">{t("tripDashboard.loading")}</div>;

    const totals = trip.totals || { total_spent: 0, budget_total: null, remaining: null, over_budget: false, by_category: {} };

    const sendChat = async (raw) => {
        const text = (raw ?? msg).trim();
        if (!text || busy) return;
        setMsg("");
        setBusy(true);
        setChat((prev) => [...prev, { role: "user", text }]);
        try {
            const res = await conciergeApi.reply(id, text);
            setChat((prev) => [...prev, { role: "assistant", text: res.assistant_message }]);
            if (res.trip) setTrip((prev) => ({ ...prev, ...res.trip }));
            setVersionsCount((v) => v + 1);
        } catch {
            toast.error(t("tripDashboard.toasts.conciergeOffline"));
        } finally {
            setBusy(false);
        }
    };

    const applyMode = async (modeKey) => {
        setModeBusy(modeKey);
        try {
            const res = await conciergeApi.mode(id, modeKey);
            if (res.trip) setTrip((prev) => ({ ...prev, ...res.trip }));
            const modeLabel = t(`tripDashboard.quickModes.${modeKey}`);
            setChat((prev) => [...prev, { role: "assistant", text: res.assistant_message || `Applied ${modeLabel} mode.` }]);
            setVersionsCount((v) => v + 1);
            toast.success(t("tripDashboard.toasts.appliedMode", { mode: modeLabel }));
        } catch {
            const modeLabel = t(`tripDashboard.quickModes.${modeKey}`);
            toast.error(t("tripDashboard.toasts.errMode", { mode: modeLabel }));
        } finally {
            setModeBusy(null);
        }
    };

    const undo = async () => {
        try {
            const res = await conciergeApi.undo(id);
            setTrip((prev) => ({ ...prev, ...res.trip }));
            setVersionsCount((v) => Math.max(0, v - 1));
            toast.success(t("tripDashboard.toasts.restored"));
        } catch (e) { toast.error(e?.response?.data?.detail || t("tripDashboard.toasts.nothingUndo")); }
    };

    const removeItem = async (itemId) => {
        const res = await conciergeApi.removeItem(id, itemId);
        setTrip((prev) => ({ ...prev, ...res.trip }));
        toast.success(t("tripDashboard.toasts.removed"));
    };

    const changeStatus = async (s) => {
        try {
            const res = await conciergeApi.setStatus(id, s);
            setTrip((prev) => ({ ...prev, ...res.trip }));
            if (s === "completed" && res.stamps_added) {
                toast.success(t(`tripDashboard.toasts.completedStamps`, { count: res.stamps_added }));
            } else {
                toast.success(t("tripDashboard.toasts.statusChanged", { status: t(`tripDashboard.statuses.${s}`, s.replace("_", " ")) }));
            }
            authApi.me().then(setUser).catch(() => {});
            window.dispatchEvent(new CustomEvent("atlas:refresh"));
        } catch { toast.error(t("tripDashboard.toasts.errStatus")); }
    };

    const shareToFeed = async () => {
        try {
            const res = await conciergeApi.shareToFeed(id, {});
            toast.success(t("tripDashboard.toasts.postedToFeed"));
            navigate(`/p/${res.post.share_slug}`);
        } catch { toast.error(t("tripDashboard.toasts.errShare")); }
    };

    const duplicateTrip = async () => {
        setDuplicatingTrip(true);
        try {
            const res = await conciergeApi.duplicateTrip(id);
            toast.success(t("tripDashboard.toasts.duplicated", "Trip duplicated successfully"));
            navigate(`/trip/${res.trip_id}`);
        } catch {
            toast.error(t("tripDashboard.toasts.errDuplicate", "Failed to duplicate trip"));
        } finally {
            setDuplicatingTrip(false);
        }
    };

    const remove = async () => {
        try {
            await conciergeApi.removeTrip(id);
            toast.success(t("tripDashboard.toasts.deleted", "Trip deleted"));
            if (trip?.status === "completed") {
                authApi.me().then(setUser).catch(() => {});
                window.dispatchEvent(new CustomEvent("atlas:refresh"));
            }
            navigate("/app?tab=trips");
        } catch { toast.error(t("tripDashboard.toasts.errDelete", "Failed to delete trip")); }
    };

    return (
        <div className="min-h-screen grain" data-testid="trip-dashboard">
            <header className="sticky top-0 z-40 glass border-b" style={{ borderColor: "rgba(255,255,255,0.06)" }}>
                <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-4 flex items-center justify-between gap-3">
                    <button onClick={() => navigate(-1)} className="flex items-center gap-3 text-left" data-testid="trip-back">
                        <ArrowLeft size={16} color="#D4AF37" />
                        <PassportLogo size={26} />
                        <div className="hidden sm:block leading-none">
                            <div className="font-serif text-base text-white">{t("tripDashboard.conciergeTitle")}</div>
                            <div className="text-[9px] tracking-[0.3em] uppercase" style={{ color: "var(--gold)" }}>{t("tripDashboard.subheader")}</div>
                        </div>
                    </button>
                    <div className="flex items-center gap-2">
                        {versionsCount > 0 && (
                            <button onClick={undo} className="btn-ghost text-xs !py-2 !px-3" data-testid="trip-undo">
                                <ArrowClockwise size={12} /> {t("tripDashboard.undo")}
                            </button>
                        )}
                        <button onClick={shareInMessages} className="btn-ghost text-xs !py-2 !px-3" data-testid="trip-send-btn">
                            <PaperPlaneRight size={12} /> Send in Messages
                        </button>
                        <button onClick={() => setShowShare(true)} className="btn-ghost text-xs !py-2 !px-3" data-testid="trip-share-btn">
                            <Share size={12} /> {t("tripDashboard.share")}
                        </button>
                        <Link to={`/traveldate?city=${encodeURIComponent((trip.destinations || [])[0] || "")}`} className="btn-ghost text-xs !py-2 !px-3" data-testid="trip-open-traveldate">
                            <span style={{ color: "#F4A28F" }}>❤</span> {t("tripDashboard.exploreTravelDate")}
                        </Link>
                        <button onClick={duplicateTrip} disabled={duplicatingTrip} className="btn-ghost text-xs !py-2 !px-3 disabled:opacity-60" data-testid="trip-duplicate">
                            {duplicatingTrip ? t("tripDashboard.duplicating", "Duplicating...") : t("tripDashboard.duplicateTrip", "Duplicate Trip")}
                        </button>
                        <button onClick={shareToFeed} className="btn-gold text-xs !py-2 !px-4" data-testid="trip-share-feed">
                            <Sparkle size={12} weight="fill" /> {t("tripDashboard.shareToFeed")}
                        </button>
                    </div>
                </div>
            </header>

            <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-8">
                {/* Hero */}
                <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="rounded-3xl overflow-hidden relative aspect-[16/9] md:aspect-[16/7] mb-8" style={{ border: "1px solid rgba(255,255,255,0.06)" }} data-testid="trip-hero">
                    <img src={trip.hero_image} alt={trip.title} className="absolute inset-0 w-full h-full object-cover" />
                    <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(11,19,32,0.2) 0%, rgba(5,10,17,0.9) 100%)" }} />
                    <div className="relative h-full p-6 md:p-10 flex flex-col justify-end">
                        <div className="flex flex-wrap items-center gap-2 mb-3">
                            <StatusPill status={trip.status} onChange={changeStatus} />
                            <span className="text-[10px] tracking-[0.3em] uppercase text-white/80">{t("tripDashboard.heroNotice")}</span>
                        </div>
                        <h1 className="font-serif text-3xl sm:text-5xl md:text-6xl text-white leading-[1] tracking-tight" data-testid="trip-title">
                            {trip.emoji || "✈️"} {trip.title}
                        </h1>
                        <div className="mt-3 flex flex-wrap gap-3 text-white/85 text-sm">
                            <MetaChip icon={Calendar} label={t("tripDashboard.days", { count: trip.days || 0 })} />
                            <MetaChip icon={Users} label={t("tripDashboard.traveler", { count: ((trip.travelers || {}).adults || 1) + ((trip.travelers || {}).children || 0) })} />
                            {trip.origin && <MetaChip icon={Airplane} label={t("tripDashboard.fromOrigin", { origin: trip.origin })} />}
                            <MetaChip icon={GlobeHemisphereWest} label={(trip.destinations || []).slice(0, 3).join(" → ") || "TBD"} />
                        </div>
                    </div>
                </motion.div>

                {/* Layout: main + sidebar */}
                <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-8">
                    <div>
                        {!!trip.booking_summary?.length && (
                            <div className="mb-6 grid gap-2 sm:grid-cols-2 lg:grid-cols-4" data-testid="trip-booking-summary">
                                {trip.booking_summary.map((entry) => (
                                    <div key={entry.kind} className="rounded-2xl p-4" style={{ background: "var(--navy-deep)", border: "1px solid rgba(255,255,255,0.06)" }}>
                                        <div className="text-sm text-white font-medium">{entry.icon} {t(`tripDashboard.kinds.${entry.kind}`, entry.label)}</div>
                                        <div className="text-[10px] tracking-[0.22em] uppercase text-white/45 mt-2">{t("tripDashboard.itemCount", { count: entry.count })}</div>
                                        <div className="mt-2 text-[11px] inline-flex px-2 py-1 rounded-full" style={{ background: entry.status_type === "estimated" ? "rgba(212,175,55,0.12)" : "rgba(159,213,208,0.12)", color: entry.status_type === "estimated" ? "var(--gold)" : "#9FD5D0", border: `1px solid ${entry.status_type === "estimated" ? "rgba(212,175,55,0.3)" : "rgba(159,213,208,0.3)"}` }}>
                                            {t(`tripDashboard.dataLabels.${entry.status}`, entry.status)}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Quick modes */}
                        <div className="mb-6">
                            <div className="overline mb-2" style={{ color: "var(--gold)" }}>{t("tripDashboard.optimise")}</div>
                            <div className="flex flex-wrap gap-2">
                                {QUICK_MODE_KEYS.map((m) => (
                                    <button
                                        key={m.k}
                                        onClick={() => applyMode(m.k)}
                                        disabled={!!modeBusy}
                                        className="px-4 py-2.5 rounded-full text-sm transition-all disabled:opacity-50"
                                        style={{
                                            background: modeBusy === m.k ? "rgba(212,175,55,0.2)" : "rgba(255,255,255,0.04)",
                                            border: "1px solid rgba(212,175,55,0.25)",
                                            color: modeBusy === m.k ? "var(--gold)" : "white",
                                        }}
                                        data-testid={`trip-mode-${m.k}`}
                                    >
                                        <span className="mr-1.5">{m.icon}</span>
                                        {modeBusy === m.k ? t("tripDashboard.reshaping") : t(`tripDashboard.quickModes.${m.k}`)}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Grouped items */}
                        {Object.keys(KIND_ICONS).map((kind) => {
                            const items = groupedItems[kind] || [];
                            if (items.length === 0) return null;
                            const Icon = KIND_ICONS[kind] || Ticket;
                            const color = KIND_COLORS[kind] || "#D4AF37";
                            const label = t(`tripDashboard.kinds.${kind}`, kind);
                            return (
                                <section key={kind} className="mb-8" data-testid={`trip-section-${kind}`}>
                                    <div className="flex items-center gap-2 mb-3">
                                        <Icon size={16} color={color} weight="fill" />
                                        <h3 className="font-serif text-2xl text-white leading-none">{label}</h3>
                                        <span className="text-[10px] tracking-[0.2em] uppercase text-white/40 ml-1">{items.length} · ${sumItems(items).toLocaleString()}</span>
                                    </div>
                                    <div className="space-y-3">
                                        {items.map((it) => (
                                            <TripItemCard key={it.id} item={it} onRemove={() => removeItem(it.id)} />
                                        ))}
                                    </div>
                                </section>
                            );
                        })}

                        {(trip.items || []).length === 0 && (
                            <div className="rounded-3xl p-8 text-center text-white/60" style={{ background: "var(--navy-deep)", border: "1px dashed rgba(255,255,255,0.1)" }}>
                                {t("tripDashboard.noItems")}
                            </div>
                        )}
                    </div>

                    {/* Sidebar: Budget + Chat */}
                    <div className="space-y-6 lg:sticky lg:top-24 self-start">
                        <BudgetCard totals={totals} onAskAlternatives={() => sendChat(t("tripDashboard.budget.alternativesPrompt"))} />

                        <div className="rounded-3xl p-4 md:p-5" style={{ background: "var(--navy-deep)", border: "1px solid rgba(212,175,55,0.25)" }}>
                            <div className="flex items-center gap-2 mb-3">
                                <Sparkle size={14} weight="fill" color="#D4AF37" />
                                <div className="font-serif text-lg text-white leading-none">{t("tripDashboard.conciergeTitle")}</div>
                            </div>
                            <div
                                ref={chatRef}
                                className="max-h-[280px] overflow-y-auto space-y-3 mb-3 pr-1"
                                data-testid="trip-chat"
                            >
                                {chat.length === 0 && (
                                    <div className="text-white/50 text-sm">
                                        {t("tripDashboard.chat.suggestionPrompt")}<br />
                                        <div className="flex flex-wrap gap-2 mt-2">
                                            {(t("tripDashboard.chat.suggestions", { returnObjects: true }) || []).map((s) => (
                                                <button key={s} onClick={() => sendChat(s)} className="text-[11px] px-2.5 py-1 rounded-full" style={{ background: "rgba(255,255,255,0.05)", color: "rgba(255,255,255,0.75)", border: "1px solid rgba(212,175,55,0.2)" }} data-testid="trip-chat-suggestion">{s}</button>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                {chat.map((m, i) => (
                                    <div key={i} className="text-[13px] leading-relaxed">
                                        <div className={m.role === "user" ? "text-white/85" : "text-white"} style={{ opacity: m.role === "user" ? 0.75 : 1 }}>
                                            <span className="text-[10px] uppercase tracking-[0.2em] mr-2" style={{ color: m.role === "user" ? "var(--gold-pale)" : "var(--gold)" }}>{m.role === "user" ? t("tripDashboard.chat.you") : t("tripDashboard.chat.ai")}</span>
                                            {m.text}
                                        </div>
                                    </div>
                                ))}
                                {busy && <div className="text-white/50 text-xs italic">{t("tripDashboard.chat.composing")}</div>}
                            </div>
                            <form onSubmit={(e) => { e.preventDefault(); sendChat(); }} className="flex gap-2" data-testid="trip-chat-form">
                                <input
                                    value={msg}
                                    onChange={(e) => setMsg(e.target.value)}
                                    placeholder={t("tripDashboard.chatPlaceholder")}
                                    className="flex-1 rounded-full px-4 py-2.5 text-sm text-white focus:outline-none"
                                    style={{ background: "rgba(0,0,0,0.35)", border: "1px solid rgba(255,255,255,0.1)" }}
                                    data-testid="trip-chat-input"
                                />
                                <button
                                    type="submit"
                                    disabled={busy || !msg.trim()}
                                    className="w-10 h-10 rounded-full grid place-items-center disabled:opacity-50"
                                    style={{ background: "var(--gold)", color: "var(--navy)" }}
                                    aria-label="Send"
                                    data-testid="trip-chat-send"
                                >
                                    <PaperPlaneRight size={12} weight="fill" />
                                </button>
                            </form>
                        </div>

                        {/* Trip meta actions */}
                        <div className="rounded-3xl p-4" style={{ background: "var(--navy-deep)", border: "1px solid rgba(255,255,255,0.06)" }}>
                            <div className="overline mb-3">{t("tripDashboard.tripStatus")}</div>
                            <div className="grid grid-cols-2 gap-1.5">
                                {STATUSES.map((s) => (
                                    <button
                                        key={s}
                                        onClick={() => changeStatus(s)}
                                        className="text-[11px] px-2 py-1.5 rounded-full capitalize transition-colors"
                                        style={{
                                            background: trip.status === s ? "rgba(212,175,55,0.16)" : "rgba(255,255,255,0.04)",
                                            border: `1px solid ${trip.status === s ? "rgba(212,175,55,0.5)" : "rgba(255,255,255,0.08)"}`,
                                            color: trip.status === s ? "var(--gold)" : "rgba(255,255,255,0.75)",
                                        }}
                                        data-testid={`trip-status-${s}`}
                                    >
                                        {t(`tripDashboard.statuses.${s}`, s.replace("_", " "))}
                                    </button>
                                ))}
                            </div>
                            <div className="flex justify-between items-start mt-4 gap-2">
                                <p className="text-[10px] text-white/40 leading-relaxed">
                                    {t("tripDashboard.completionNote")}
                                </p>
                                <button onClick={remove} className="text-[11px] px-3 py-1.5 rounded-full shrink-0" style={{ background: "rgba(255,120,120,0.14)", color: "#FF8888", border: "1px solid rgba(255,120,120,0.3)" }} data-testid="trip-delete">
                                    <Trash size={11} className="inline mr-1" /> {t("tripDashboard.delete", "Delete")}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {showShare && <ShareModal tripId={id} slug={trip.share_slug} enabled={trip.share_enabled} onClose={() => setShowShare(false)} onUpdate={(s) => setTrip((prev) => ({ ...prev, share_slug: s.slug, share_enabled: s.share_enabled }))} />}
        </div>
    );
}

export function MetaChip({ icon: Icon, label }) {
    return (
        <span className="inline-flex items-center gap-1.5 text-[12px]">
            <Icon size={12} weight="fill" color="#D4AF37" />
            {label}
        </span>
    );
}

export function StatusPill({ status, onChange }) {
    const { t } = useTranslation();
    const tone = status === "completed" ? { bg: "rgba(159,213,208,0.16)", fg: "#9FD5D0", border: "rgba(159,213,208,0.5)" }
        : status === "cancelled" ? { bg: "rgba(255,120,120,0.14)", fg: "#FF8888", border: "rgba(255,120,120,0.4)" }
        : { bg: "rgba(212,175,55,0.16)", fg: "var(--gold)", border: "rgba(212,175,55,0.5)" };
    return (
        <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[10px] uppercase tracking-[0.25em]"
              style={{ background: tone.bg, color: tone.fg, border: `1px solid ${tone.border}` }} data-testid="trip-status-pill">
            {t(`tripDashboard.statuses.${status || "planning"}`, (status || "planning").replace("_", " "))}
        </span>
    );
}

export function TripItemCard({ item, onRemove }) {
    const { t } = useTranslation();
    const Icon = KIND_ICONS[item.kind] || Ticket;
    const color = KIND_COLORS[item.kind] || "#D4AF37";
    return (
        <motion.div
            initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
            className="rounded-2xl p-4 relative group"
            style={{ background: "var(--navy-deep)", border: "1px solid rgba(255,255,255,0.06)" }}
            data-testid={`trip-item-${item.id}`}
        >
            <div className="flex items-start gap-3">
                <div
                    className="w-11 h-11 rounded-2xl grid place-items-center shrink-0"
                    style={{ background: "rgba(212,175,55,0.1)", border: `1px solid ${color}40` }}
                >
                    <Icon size={16} color={color} weight="fill" />
                </div>
                <div className="flex-1 min-w-0 pr-8">
                    <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                            <div className="text-white font-medium leading-tight line-clamp-1">{item.title}</div>
                            {item.subtitle && <div className="text-white/60 text-[13px] mt-1 line-clamp-2">{item.subtitle}</div>}
                        </div>
                        {item.price_estimate ? (
                            <div className="text-right shrink-0">
                                <div className="font-serif text-lg" style={{ color: "var(--gold)" }}>${Math.round(item.price_estimate).toLocaleString()}</div>
                                <div className="text-[9px] uppercase tracking-[0.2em] text-white/40">{t("tripDashboard.est")}</div>
                            </div>
                        ) : null}
                    </div>
                    <div className="flex flex-wrap items-center gap-2 mt-2">
                        {item.day && <span className="text-[10px] uppercase tracking-[0.15em] text-white/50">{t("tripDashboard.day", { day: item.day })}</span>}
                        {item.city && <span className="text-[10px] uppercase tracking-[0.15em] text-white/50">· {item.city}</span>}
                        <span
                            className="text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 rounded-full"
                            style={{ background: "rgba(159,213,208,0.08)", color: "#9FD5D0", border: "1px solid rgba(159,213,208,0.2)" }}
                        >
                            {t(`tripDashboard.dataLabels.${item.data_label}`, item.data_label || "Estimated")}
                        </span>
                    </div>
                    {item.note && <div className="text-white/55 text-[12px] mt-2 italic">{item.note}</div>}
                </div>
            </div>
            {onRemove && (
                <button
                    onClick={onRemove}
                    className="absolute top-3 right-3 w-7 h-7 rounded-full grid place-items-center opacity-0 group-hover:opacity-100 transition-opacity"
                    style={{ background: "rgba(0,0,0,0.4)", border: "1px solid rgba(255,255,255,0.08)" }}
                    data-testid={`trip-item-remove-${item.id}`}
                    aria-label="Remove"
                >
                    <Trash size={11} color="rgba(255,120,120,0.9)" />
                </button>
            )}
        </motion.div>
    );
}

export function BudgetCard({ totals, onAskAlternatives }) {
    const { t } = useTranslation();
    const pct = totals.budget_total ? Math.min(100, Math.round((totals.total_spent / totals.budget_total) * 100)) : 0;
    return (
        <div className="rounded-3xl p-5" style={{ background: "var(--navy-deep)", border: "1px solid rgba(212,175,55,0.25)" }} data-testid="trip-budget">
            <div className="flex items-center gap-2 mb-3">
                <Wallet size={14} weight="fill" color="#D4AF37" />
                <div className="font-serif text-lg text-white leading-none">{t("tripDashboard.budget.title")}</div>
            </div>
            <div className="flex items-baseline justify-between">
                <div>
                    <div className="font-serif text-3xl" style={{ color: totals.over_budget ? "#FF8888" : "var(--gold)" }}>
                        ${Math.round(totals.total_spent).toLocaleString()}
                    </div>
                    <div className="text-[10px] tracking-[0.25em] uppercase text-white/50 mt-1">
                        {totals.budget_total ? t("tripDashboard.budget.ofBudget", { total: Math.round(totals.budget_total).toLocaleString() }) : t("tripDashboard.budget.aiEstimated")}
                    </div>
                </div>
                {totals.budget_total && (
                    <div className="text-right">
                        <div className="font-serif text-lg" style={{ color: totals.over_budget ? "#FF8888" : "#9FD5D0" }}>
                            {totals.over_budget ? "-" : ""}${Math.abs(Math.round(totals.remaining || 0)).toLocaleString()}
                        </div>
                        <div className="text-[10px] tracking-[0.25em] uppercase text-white/50">{totals.over_budget ? t("tripDashboard.budget.over") : t("tripDashboard.budget.remaining")}</div>
                    </div>
                )}
            </div>
            {totals.budget_total && (
                <div className="mt-4 h-2 rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.06)" }}>
                    <div
                        className="h-full rounded-full transition-all"
                        style={{
                            width: `${pct}%`,
                            background: totals.over_budget ? "linear-gradient(90deg, #FF8888, #F4A28F)" : "linear-gradient(90deg, #D4AF37, #F4D06F)",
                        }}
                    />
                </div>
            )}
            <div className="grid grid-cols-2 gap-1.5 mt-4">
                {Object.keys(KIND_ICONS).map((kind) => {
                    const v = totals.by_category?.[kind] || 0;
                    if (v === 0) return null;
                    const Icon = KIND_ICONS[kind] || Ticket;
                    const color = KIND_COLORS[kind] || "#D4AF37";
                    const label = t(`tripDashboard.kinds.${kind}`, kind);
                    return (
                        <div key={kind} className="flex items-center justify-between text-[11px] px-2 py-1 rounded-lg" style={{ background: "rgba(255,255,255,0.03)" }}>
                            <span className="flex items-center gap-1.5 text-white/70"><Icon size={10} weight="fill" color={color} />{label}</span>
                            <span className="text-white/85">${Math.round(v)}</span>
                        </div>
                    );
                })}
            </div>
            {totals.over_budget && onAskAlternatives && (
                <button
                    onClick={onAskAlternatives}
                    className="mt-4 w-full text-sm py-2.5 rounded-full inline-flex items-center justify-center gap-2"
                    style={{ background: "rgba(255,120,120,0.14)", color: "#FF8888", border: "1px solid rgba(255,120,120,0.35)" }}
                    data-testid="trip-budget-alternatives"
                >
                    <Sparkle size={12} weight="fill" /> {t("tripDashboard.budget.showCheaper")}
                </button>
            )}
        </div>
    );
}

function ShareModal({ tripId, slug, enabled, onClose, onUpdate }) {
    const { t } = useTranslation();
    const [busy, setBusy] = useState(false);
    const url = slug ? `${window.location.origin}/trip/share/${slug}` : "";
    useEffect(() => {
        const onKey = (e) => { if (e.key === "Escape") onClose(); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [onClose]);
    const copy = async () => {
        try {
            if (navigator.clipboard && window.isSecureContext) {
                await navigator.clipboard.writeText(url);
                toast.success(t("tripDashboard.toasts.copied"));
                return;
            }
            throw new Error("no-clipboard");
        } catch {
            try {
                const ta = document.createElement("textarea");
                ta.value = url;
                ta.style.position = "fixed";
                ta.style.opacity = "0";
                document.body.appendChild(ta);
                ta.select();
                document.execCommand("copy");
                document.body.removeChild(ta);
                toast.success(t("tripDashboard.toasts.copied"));
            } catch {
                toast.error(t("tripDashboard.toasts.copyFailed"));
            }
        }
    };
    const toggle = async (state) => {
        setBusy(true);
        try {
            const res = await conciergeApi.share(tripId, state);
            onUpdate({ slug: res.slug || slug, share_enabled: !!res.share_enabled });
            if (state) toast.success(t("tripDashboard.toasts.linkEnabled"));
            else toast.success(t("tripDashboard.toasts.linkDisabled"));
        } catch { toast.error(t("tripDashboard.toasts.errShareToggle")); }
        finally { setBusy(false); }
    };
    return (
        <AnimatePresence>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                onClick={onClose}
                className="fixed inset-0 z-[80] flex items-center justify-center p-4"
                style={{ background: "rgba(5,10,17,0.85)", backdropFilter: "blur(8px)" }}
                data-testid="share-modal"
            >
                <motion.div onClick={(e) => e.stopPropagation()} initial={{ y: 16, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="rounded-3xl max-w-md w-full p-7" style={{ background: "var(--navy)", border: "1px solid rgba(212,175,55,0.3)" }}>
                    <div className="overline mb-2">{t("tripDashboard.shareModal.overline")}</div>
                    <h3 className="font-serif text-2xl text-white mb-2">{t("tripDashboard.shareModal.title")}</h3>
                    <p className="text-white/60 text-sm mb-5">{t("tripDashboard.shareModal.sub")}</p>
                    {enabled && slug ? (
                        <div className="mb-4">
                            <div className="text-[11px] uppercase tracking-[0.25em] text-white/50 mb-1">{t("tripDashboard.shareModal.publicLink")}</div>
                            <div className="rounded-full px-4 py-3 text-sm text-white overflow-x-auto whitespace-nowrap" style={{ background: "rgba(0,0,0,0.4)", border: "1px solid rgba(212,175,55,0.3)" }}>
                                {url}
                            </div>
                            <div className="flex gap-2 mt-3">
                                <button onClick={copy} className="btn-ghost flex-1 justify-center" data-testid="share-copy">{t("tripDashboard.shareModal.copy")}</button>
                                <button onClick={() => toggle(false)} disabled={busy} className="btn-ghost flex-1 justify-center" data-testid="share-disable">{t("tripDashboard.shareModal.turnOff")}</button>
                            </div>
                        </div>
                    ) : (
                        <button onClick={() => toggle(true)} disabled={busy} className="btn-gold w-full justify-center" data-testid="share-enable">
                            {busy ? t("tripDashboard.shareModal.generating") : t("tripDashboard.shareModal.generateLink")}
                        </button>
                    )}
                    <button onClick={onClose} className="w-full mt-3 text-white/60 text-xs tracking-[0.2em] uppercase">{t("tripDashboard.shareModal.close")}</button>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
}

export function sumItems(items) {
    return items.reduce((s, i) => s + (i.price_estimate || 0), 0);
}
